type LogLevel = 'debug' | 'log' | 'info' | 'warn' | 'error';

interface Logger {
  log: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
}

const noop = () => {};

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  log: 1,
  info: 2,
  warn: 3,
  error: 4,
};

const currentLogLevel: LogLevel = 'log';

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[currentLogLevel];
}

const createLogger = (): Logger => {
  const isDev = typeof __DEV__ !== 'undefined' ? __DEV__ : process.env.NODE_ENV !== 'production';

  // Production keeps warn and error. Silencing them cost us a day on the
  // July 2026 "Failed to create post" outage: every web post create was
  // being denied by Firestore and the client threw the real reason away, so
  // the only signal left was a screenshot from the client. Chatter (debug /
  // log / info) still goes quiet.
  if (!isDev) {
    return {
      log: noop,
      info: noop,
      debug: noop,
      warn: (...args: unknown[]) => console.warn(...args),
      error: (...args: unknown[]) => console.error(...args),
    };
  }

  return {
    log: (...args: unknown[]) => shouldLog('log') && console.log(...args),
    info: (...args: unknown[]) => shouldLog('info') && console.info(...args),
    warn: (...args: unknown[]) => shouldLog('warn') && console.warn(...args),
    error: (...args: unknown[]) => shouldLog('error') && console.error(...args),
    debug: (...args: unknown[]) => shouldLog('debug') && console.debug(...args),
  };
};

export const createPrefixedLogger = (prefix: string): Logger => {
  const baseLogger = createLogger();
  const addPrefix = (args: unknown[]): unknown[] => [`[${prefix}]`, ...args];

  return {
    log: (...args: unknown[]) => baseLogger.log(...addPrefix(args)),
    info: (...args: unknown[]) => baseLogger.info(...addPrefix(args)),
    warn: (...args: unknown[]) => baseLogger.warn(...addPrefix(args)),
    error: (...args: unknown[]) => baseLogger.error(...addPrefix(args)),
    debug: (...args: unknown[]) => baseLogger.debug(...addPrefix(args)),
  };
};
